import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  MetricCard,
  StockBadge,
} from '@beautyburst/ui';
import { minTouchTarget, semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type {
  AfterHoursEnrollment,
  ColumnMapping,
  ImportJob,
  ImportRow,
  ImportTemplate,
  JsonRecord,
  RetailerApiClient,
  RetailerDashboard,
  RetailerOrderSummary,
  RetailerPayoutOverview,
  RetailerStoreProfile,
} from './api-client.ts';
import { retailerHref } from './base-path.ts';

// A grid item defaults to min-width:auto, so it grows to its max-content width
// rather than the column width. Without the explicit minmax(0, 1fr) track, a
// wide DataTable stretched the page container past the viewport at 390px and
// pushed the heading and body copy off screen, and the table's own
// overflow-x:auto never engaged because its wrapper had expanded to fit.
const pageStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: space[6],
} as const;
const gridStyle = {
  display: 'grid',
  gap: space[4],
  // min() lets the track shrink below 13rem rather than overflow a narrow page.
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(13rem, 100%), 1fr))',
} as const;
const statuses: Record<string, readonly string[]> = {
  PENDING: ['ACCEPTED', 'CANCELLED'],
  ACCEPTED: ['PREPARING', 'CANCELLED', 'FAILED'],
  PREPARING: ['READY_FOR_PICKUP', 'CANCELLED', 'FAILED'],
  READY_FOR_PICKUP: ['PICKED_UP', 'CANCELLED', 'FAILED'],
  PICKED_UP: ['IN_TRANSIT', 'FAILED'],
  IN_TRANSIT: ['DELIVERED', 'FAILED'],
  DELIVERED: [],
  FAILED: [],
  CANCELLED: [],
};
function record(value: unknown): JsonRecord {
  return value !== null && typeof value === 'object' ? (value as JsonRecord) : {};
}
function text(value: unknown, fallback = '—') {
  return typeof value === 'string' && value !== '' ? value : fallback;
}
function number(value: unknown, fallback = 0) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function money(amount: unknown, currency: unknown) {
  return typeof amount === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: text(currency, 'USD') }).format(
        amount / 100,
      )
    : 'Not available';
}
function statusVariant(status: string): 'neutral' | 'warning' | 'success' | 'danger' {
  return /(FAILED|CANCELLED|REJECTED)/.test(status)
    ? 'danger'
    : /(READY|DELIVERED|FINALISED|APPROVED)/.test(status)
      ? 'success'
      : /(PENDING|DRAFT|PREPARING)/.test(status)
        ? 'warning'
        : 'neutral';
}
function PageTitle({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div>
      <h1 style={{ ...typeStyle('headingMd'), color: semanticColor.textPrimary, margin: 0 }}>
        {title}
      </h1>
      {children}
    </div>
  );
}
function Notice({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        padding: space[4],
        color: semanticColor.textPrimary,
        background: semanticColor.surfaceSubtle,
      }}
    >
      {children}
    </p>
  );
}
function StateMessage({ message }: { message: string | null }) {
  return message === null ? null : (
    <p role="status" aria-live="polite">
      {message}
    </p>
  );
}
function Skeleton({ label = 'Loading operational data' }: { label?: string }) {
  return (
    <section aria-label={label} style={pageStyle}>
      <div style={{ height: '2rem', background: semanticColor.surfaceSubtle }} />
      <div style={gridStyle}>
        {[1, 2, 3].map((n) => (
          <div key={n} style={{ height: '8rem', background: semanticColor.surfaceSubtle }} />
        ))}
      </div>
    </section>
  );
}

export function DashboardScreen({
  dashboard,
  loadError = false,
}: {
  dashboard?: RetailerDashboard;
  loadError?: boolean;
}) {
  if (loadError)
    return (
      <ErrorState
        title="Store health unavailable"
        description="We could not load operational information. Try again shortly."
      />
    );
  if (dashboard === undefined) return <Skeleton label="Loading store health" />;
  const rows = Object.entries(dashboard.fulfilments?.byStatus ?? {});
  return (
    <div style={pageStyle}>
      <PageTitle title="Store health">
        <p style={{ color: semanticColor.textSecondary }}>
          Live operational information from your retailer account.
        </p>
      </PageTitle>
      <div style={gridStyle}>
        <MetricCard
          label="Open fulfilments"
          value={String(dashboard.fulfilments?.openCount ?? 0)}
          helpText="Across active fulfilment states"
        />
        <MetricCard
          label="Threshold breaches"
          value={String(dashboard.inventory?.thresholdBreachCount ?? 0)}
          helpText="Low or critical stock balances"
        />
        <MetricCard
          label="Next payout"
          value={
            dashboard.nextPayout === null || dashboard.nextPayout === undefined
              ? 'Not available'
              : dashboard.nextPayout.status
          }
          helpText={
            dashboard.nextPayout === null || dashboard.nextPayout === undefined
              ? 'No payout run is available'
              : money(dashboard.nextPayout.payoutMinor, dashboard.nextPayout.currency)
          }
        />
      </div>
      <div style={gridStyle}>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Open orders by status</h2>
          {rows.length === 0 ? (
            <EmptyState
              title="No open fulfilments"
              description="New customer orders will appear here when they need store action."
            />
          ) : (
            <DataTable
              caption="Open fulfilments by status"
              rows={rows}
              rowKey={([status]) => status}
              columns={[
                {
                  key: 'status',
                  header: 'Status',
                  render: ([status]) => <Badge variant={statusVariant(status)}>{status}</Badge>,
                },
                {
                  key: 'count',
                  header: 'Orders',
                  numeric: true,
                  render: ([, count]) => String(count),
                },
              ]}
            />
          )}
        </Card>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Store availability</h2>
          <ul
            aria-label="Store availability"
            style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
          >
            {(dashboard.locations ?? []).map((location) => (
              <li key={location.id}>
                <strong>{location.name}</strong>
                <br />
                <Badge variant={location.storeOpen ? 'success' : 'warning'}>
                  {location.storeOpen ? 'Open' : 'Closed'}
                </Badge>{' '}
                <span>
                  {location.extendedHours.enabled
                    ? location.extendedHours.paused
                      ? 'Extended hours paused'
                      : 'Extended hours enabled'
                    : 'Extended hours not approved'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

export function OrdersScreen({ orders = [] }: { orders?: readonly RetailerOrderSummary[] }) {
  return (
    <div style={pageStyle}>
      <PageTitle title="Orders">
        <p style={{ color: semanticColor.textSecondary }}>
          Only your store’s fulfilments are shown.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Incoming fulfilments</h2>
        <DataTable
          caption="Store fulfilments"
          rows={orders}
          rowKey={(row) => row.id}
          emptyMessage="No fulfilments are waiting for this store."
          columns={[
            {
              key: 'id',
              header: 'Fulfilment',
              render: (row) => <a href={retailerHref(`/orders/${row.id}`)}>{row.id}</a>,
            },
            { key: 'mode', header: 'Mode', render: (row) => row.fulfillment_mode },
            {
              key: 'status',
              header: 'Current state',
              render: (row) => <Badge variant={statusVariant(row.status)}>{row.status}</Badge>,
            },
          ]}
        />
      </Card>
    </div>
  );
}

export function OrderDetailScreen({
  order,
  client,
}: {
  order?: RetailerOrderSummary;
  client: RetailerApiClient;
}) {
  const [current, setCurrent] = useState(order);
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  if (current === undefined)
    return (
      <EmptyState
        title="Fulfilment unavailable"
        description="This fulfilment was not found in your retailer scope."
      />
    );
  const next = statuses[current.status] ?? [];
  const update = async (status: string) => {
    setWorking(true);
    setMessage(null);
    try {
      const updated = await client.orders.transition(
        current.id,
        status as RetailerOrderSummary['status'],
      );
      setCurrent(updated);
      setMessage(`Fulfilment advanced to ${status.replaceAll('_', ' ').toLowerCase()}.`);
    } catch {
      setMessage('We could not update this fulfilment. Its current state has not changed.');
    } finally {
      setWorking(false);
    }
  };
  return (
    <div style={pageStyle}>
      <PageTitle title={`Fulfilment ${current.id}`}>
        <p style={{ color: semanticColor.textSecondary }}>
          {current.fulfillment_mode} · Current state:{' '}
          <Badge variant={statusVariant(current.status)}>{current.status}</Badge>
        </p>
      </PageTitle>
      <Notice>
        If something is wrong with your order, contact Beauty Burst Support so we can review the
        issue and determine the appropriate resolution.
      </Notice>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Legal next transitions</h2>
        {next.length === 0 ? (
          <EmptyState
            title="No retailer action available"
            description="This fulfilment is in a terminal state."
          />
        ) : (
          <div style={{ ...gridStyle, marginTop: space[4] }}>
            {next.map((status) => (
              <Button
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                key={status}
                variant={status === 'ACCEPTED' ? 'plum' : 'secondary'}
                loading={working}
                onClick={() => void update(status)}
              >
                {status.replaceAll('_', ' ')}
              </Button>
            ))}
          </div>
        )}
        <StateMessage message={message} />
      </Card>
    </div>
  );
}

export function CatalogScreen({
  listings = [],
  client,
}: {
  listings?: readonly JsonRecord[];
  client: RetailerApiClient;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const submitListing = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const listingId = String(form.get('listingId') ?? '');
    const optionalDate = (name: string) => {
      const value = String(form.get(name) ?? '');
      return value === '' ? null : new Date(value).toISOString();
    };
    const payload: JsonRecord = {
      locationId: String(form.get('locationId')),
      productId: String(form.get('productId')),
      variantId: String(form.get('variantId')),
      sellerDisplayName: String(form.get('sellerDisplayName')),
      sellerSku: String(form.get('sellerSku')),
      priceCents: Number(form.get('priceCents')),
      salePriceCents: form.get('salePriceCents') === '' ? null : Number(form.get('salePriceCents')),
      saleStartsAt: optionalDate('saleStartsAt'),
      saleEndsAt: optionalDate('saleEndsAt'),
      status: String(form.get('status')),
      pickupEligible: form.get('pickupEligible') === 'on',
      deliveryEligible: form.get('deliveryEligible') === 'on',
      overnightEligible: form.get('overnightEligible') === 'on',
    };
    setWorking(true);
    try {
      if (listingId) await client.catalog.update(listingId, payload);
      else await client.catalog.create(payload);
      setMessage(
        listingId
          ? 'Listing pricing and availability saved.'
          : 'Listing created. Refresh the page to see server state.',
      );
    } catch {
      setMessage('The listing could not be saved. Check required product and location IDs.');
    } finally {
      setWorking(false);
    }
  };
  const action = async (id: string, command: 'archive' | 'restore') => {
    setWorking(true);
    try {
      await client.catalog[command](id);
      setMessage(
        `Listing ${command === 'archive' ? 'archived' : 'restored'}. Refresh the page to view server state.`,
      );
    } catch {
      setMessage('The listing could not be updated.');
    } finally {
      setWorking(false);
    }
  };
  const media = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setWorking(true);
    try {
      await client.catalog.attachMedia(String(data.get('listingId')), {
        storageKey: String(data.get('storageKey')),
        altText: String(data.get('altText')) || null,
        mediaKind: String(data.get('mediaKind')) || 'IMAGE',
        sortOrder: Number(data.get('sortOrder')) || 0,
      });
      setMessage('Media reference attached to the listing.');
    } catch {
      setMessage('Media could not be attached.');
    } finally {
      setWorking(false);
    }
  };
  const removeMedia = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await client.catalog.removeMedia(String(data.get('listingId')), String(data.get('mediaId')));
      setMessage('Media reference removed from the listing.');
    } catch {
      setMessage('Media could not be removed.');
    }
  };
  return (
    <div style={pageStyle}>
      <PageTitle title="Catalog">
        <p style={{ color: semanticColor.textSecondary }}>
          Manage listings, pricing, sale windows, availability, and media references.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Listings</h2>
        <DataTable
          caption="Retailer listings"
          rows={listings}
          rowKey={(row) => text(row.id)}
          emptyMessage="No listings are available for this store."
          columns={[
            {
              key: 'listing',
              header: 'Listing',
              render: (row) => text(row.seller_sku, text(row.id)),
            },
            {
              key: 'price',
              header: 'Price',
              render: (row) => money(row.price_cents, row.currency),
            },
            {
              key: 'sale',
              header: 'Sale',
              render: (row) =>
                row.sale_price_cents === null
                  ? 'No sale'
                  : money(row.sale_price_cents, row.currency),
            },
            {
              key: 'availability',
              header: 'Availability',
              render: (row) => (
                <Badge variant={statusVariant(text(row.status))}>{text(row.status)}</Badge>
              ),
            },
            {
              key: 'actions',
              header: 'Action',
              render: (row) => (
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  variant="secondary"
                  loading={working}
                  onClick={() =>
                    void action(
                      text(row.id),
                      text(row.status) === 'ARCHIVED' ? 'restore' : 'archive',
                    )
                  }
                >
                  {text(row.status) === 'ARCHIVED' ? 'Restore' : 'Archive'}
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Create or update listing</h2>
        <p style={{ color: semanticColor.textSecondary }}>
          Enter a listing ID to update it; leave it blank to create a listing.
        </p>
        <form
          onSubmit={(event) => void submitListing(event)}
          style={{ display: 'grid', gap: space[3], marginTop: space[4] }}
        >
          <Field id="listing-id" name="listingId" label="Listing ID (for update)" />
          <Field id="listing-location" name="locationId" label="Location ID" required />
          <Field id="listing-product" name="productId" label="Product ID" required />
          <Field id="listing-variant" name="variantId" label="Variant ID" required />
          <Field id="listing-name" name="sellerDisplayName" label="Listing name" required />
          <Field id="listing-sku" name="sellerSku" label="Seller SKU" required />
          <Field
            id="listing-price"
            name="priceCents"
            label="Price (cents)"
            inputMode="numeric"
            required
          />
          <Field
            id="listing-sale-price"
            name="salePriceCents"
            label="Sale price (cents)"
            inputMode="numeric"
          />
          <Field
            id="listing-sale-start"
            name="saleStartsAt"
            label="Sale starts"
            type="datetime-local"
          />
          <Field id="listing-sale-end" name="saleEndsAt" label="Sale ends" type="datetime-local" />
          <label>
            Status{' '}
            <select name="status" defaultValue="ACTIVE">
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </label>
          <label>
            <input name="pickupEligible" type="checkbox" defaultChecked /> Pickup available
          </label>
          <label>
            <input name="deliveryEligible" type="checkbox" defaultChecked /> Delivery available
          </label>
          <label>
            <input name="overnightEligible" type="checkbox" /> Beauty After Hours eligible
          </label>
          <Button
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            type="submit"
            variant="plum"
            loading={working}
          >
            Save listing
          </Button>
        </form>
      </Card>
      <div style={gridStyle}>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Attach listing media</h2>
          <p style={{ color: semanticColor.textSecondary }}>
            Media storage requires an approved provider reference. UI BUILT — INTEGRATION ACCOUNT
            REQUIRED
          </p>
          <form onSubmit={(event) => void media(event)} style={{ display: 'grid', gap: space[3] }}>
            <Field id="listing-media-id" name="listingId" label="Listing ID" required />
            <Field
              id="listing-media-key"
              name="storageKey"
              label="Approved media storage key"
              required
            />
            <Field id="listing-media-alt" name="altText" label="Image description" />
            <label>
              Media type{' '}
              <select name="mediaKind">
                <option value="IMAGE">Image</option>
                <option value="VIDEO">Video</option>
              </select>
            </label>
            <Field
              id="listing-media-order"
              name="sortOrder"
              label="Display order"
              inputMode="numeric"
              defaultValue="0"
            />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="plum"
              loading={working}
            >
              Attach media
            </Button>
          </form>
        </Card>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Remove listing media</h2>
          <form
            onSubmit={(event) => void removeMedia(event)}
            style={{ display: 'grid', gap: space[3] }}
          >
            <Field id="remove-media-listing" name="listingId" label="Listing ID" required />
            <Field id="remove-media-id" name="mediaId" label="Media ID" required />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="secondary"
            >
              Remove media
            </Button>
          </form>
        </Card>
      </div>
      <StateMessage message={message} />
    </div>
  );
}

export function InventoryScreen({
  balances = [],
  client,
}: {
  balances?: readonly JsonRecord[];
  client: RetailerApiClient;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [movements, setMovements] = useState<JsonRecord[]>([]);
  const [working, setWorking] = useState(false);
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setWorking(true);
    try {
      await client.inventory.setOnHand(String(f.get('balanceId')), {
        onHand: Number(f.get('onHand')),
        reason: String(f.get('reason')),
        movementType: 'MANUAL_ADJUSTMENT',
      });
      setMessage('On-hand balance saved.');
    } catch {
      setMessage('The on-hand adjustment could not be saved.');
    } finally {
      setWorking(false);
    }
  };
  const thresholds = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setWorking(true);
    try {
      await client.inventory.setThresholds(String(f.get('balanceId')), {
        lowStockThreshold: Number(f.get('lowStockThreshold')),
        criticalStockThreshold: Number(f.get('criticalStockThreshold')),
      });
      setMessage('Inventory thresholds saved.');
    } catch {
      setMessage('The inventory thresholds could not be saved.');
    } finally {
      setWorking(false);
    }
  };
  const viewMovements = async (id: string) => {
    try {
      setMovements(await client.inventory.movements(id));
      setMessage('Movement history loaded.');
    } catch {
      setMessage('Movement history is unavailable.');
    }
  };
  return (
    <div style={pageStyle}>
      <PageTitle title="Inventory">
        <p style={{ color: semanticColor.textSecondary }}>
          Review balances and thresholds before accepting more orders.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Balances</h2>
        <DataTable
          caption="Inventory balances"
          rows={balances}
          rowKey={(row) => text(row.id)}
          emptyMessage="No inventory balances are available."
          columns={[
            {
              key: 'product',
              header: 'Listing',
              render: (row) => text(row.product_name, text(row.seller_sku)),
            },
            {
              key: 'onhand',
              header: 'On hand',
              numeric: true,
              render: (row) => String(number(row.on_hand)),
            },
            {
              key: 'available',
              header: 'Available',
              numeric: true,
              render: (row) => String(number(row.available)),
            },
            {
              key: 'threshold',
              header: 'Threshold',
              numeric: true,
              render: (row) => String(number(row.low_stock_threshold)),
            },
            {
              key: 'status',
              header: 'Status',
              render: (row) => (
                <StockBadge
                  state={
                    text(row.status) === 'OUT'
                      ? 'OUT_OF_STOCK'
                      : text(row.status) === 'CRITICAL'
                        ? 'CRITICAL'
                        : text(row.status) === 'LOW'
                          ? 'LOW'
                          : 'IN_STOCK'
                  }
                />
              ),
            },
            {
              key: 'movement',
              header: 'History',
              render: (row) => (
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  variant="secondary"
                  onClick={() => void viewMovements(text(row.id))}
                >
                  View history
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <div style={gridStyle}>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Adjust on-hand stock</h2>
          <form
            onSubmit={(event) => void save(event)}
            style={{ display: 'grid', gap: space[3], marginTop: space[4] }}
          >
            <Field id="balance-id" name="balanceId" label="Inventory balance ID" required />
            <Field
              id="on-hand"
              name="onHand"
              label="On-hand quantity"
              inputMode="numeric"
              required
            />
            <Field id="adjustment-reason" name="reason" label="Reason" required />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="plum"
              loading={working}
            >
              Save stock level
            </Button>
          </form>
        </Card>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Thresholds</h2>
          <form
            onSubmit={(event) => void thresholds(event)}
            style={{ display: 'grid', gap: space[3], marginTop: space[4] }}
          >
            <Field
              id="threshold-balance-id"
              name="balanceId"
              label="Inventory balance ID"
              required
            />
            <Field
              id="low-stock-threshold"
              name="lowStockThreshold"
              label="Low-stock threshold"
              inputMode="numeric"
              required
            />
            <Field
              id="critical-stock-threshold"
              name="criticalStockThreshold"
              label="Critical threshold"
              inputMode="numeric"
              required
            />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="secondary"
              loading={working}
            >
              Save thresholds
            </Button>
          </form>
        </Card>
      </div>
      {movements.length > 0 && (
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Movement history</h2>
          <DataTable
            caption="Inventory movement history"
            rows={movements}
            rowKey={(row) => text(row.id)}
            emptyMessage="No movements."
            columns={[
              { key: 'type', header: 'Type', render: (row) => text(row.movement_type) },
              {
                key: 'quantity',
                header: 'Quantity',
                render: (row) => String(number(row.quantity)),
              },
              { key: 'when', header: 'When', render: (row) => text(row.created_at) },
            ]}
          />
        </Card>
      )}
      <StateMessage message={message} />
    </div>
  );
}

export function StoreProfileScreen({
  profile,
  client,
}: {
  profile?: RetailerStoreProfile;
  client: RetailerApiClient;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  if (profile === undefined) return <Skeleton label="Loading store information" />;
  const location = record(profile.locations[0]);
  const locationId = text(location.id, '');
  const saveInfo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setWorking(true);
    try {
      await client.store.updateLocation(locationId, {
        name: String(f.get('name')),
        timezone: String(f.get('timezone')),
        addressLine1: String(f.get('addressLine1')),
        city: String(f.get('city')),
        region: String(f.get('region')),
        postalCode: String(f.get('postalCode')),
        countryCode: String(f.get('countryCode')),
        phone: String(f.get('phone')),
        email: String(f.get('email')),
        pickupEnabled: f.get('pickupEnabled') === 'on',
        deliveryEnabled: f.get('deliveryEnabled') === 'on',
      });
      setMessage('Store location details saved.');
    } catch {
      setMessage('Store details could not be saved.');
    } finally {
      setWorking(false);
    }
  };
  const saveHours = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const windows = Array.from({ length: 7 }, (_, dayOfWeek) => ({
      dayOfWeek,
      opensMinute: Number(f.get(`open-${dayOfWeek}`)),
      closesMinute: Number(f.get(`close-${dayOfWeek}`)),
      windowKind: 'STANDARD',
    })).filter(
      (window) =>
        Number.isInteger(window.opensMinute) &&
        Number.isInteger(window.closesMinute) &&
        window.closesMinute > window.opensMinute,
    );
    setWorking(true);
    try {
      await client.store.saveHours({
        locationId,
        kind: 'PUBLIC_STORE',
        mode: windows.length === 0 ? 'CLOSED' : 'SCHEDULED',
        windows,
      });
      setMessage('Weekly store schedule saved.');
    } catch {
      setMessage('Weekly schedule could not be saved.');
    } finally {
      setWorking(false);
    }
  };
  const holiday = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    try {
      await client.store.addHoliday({
        locationId,
        calendarDate: String(f.get('date')),
        label: String(f.get('label')),
        isClosed: f.get('closed') === 'on',
        opensMinute: f.get('closed') === 'on' ? null : Number(f.get('open')),
        closesMinute: f.get('closed') === 'on' ? null : Number(f.get('close')),
      });
      setMessage('Holiday override saved.');
    } catch {
      setMessage('Holiday override could not be saved.');
    }
  };
  const closure = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    try {
      await client.store.addClosure({
        locationId,
        reason: String(f.get('reason')),
        reasonCode: 'STORE_CLOSURE',
        startsAt: new Date(String(f.get('startsAt'))).toISOString(),
        endsAt:
          String(f.get('endsAt')) === '' ? null : new Date(String(f.get('endsAt'))).toISOString(),
      });
      setMessage('Closure saved.');
    } catch {
      setMessage('Closure could not be saved.');
    }
  };
  const pauseHours = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    try {
      const until = String(f.get('pausedUntil'));
      await client.store.pauseHours({
        locationId,
        kind: 'PUBLIC_STORE',
        pausedUntil: until === '' ? null : new Date(until).toISOString(),
        reason: String(f.get('reason')) || null,
      });
      setMessage(until === '' ? 'Store availability resumed.' : 'Store availability pause saved.');
    } catch {
      setMessage('Store availability pause could not be saved.');
    }
  };
  return (
    <div style={pageStyle}>
      <PageTitle title="Store information and hours">
        <p style={{ color: semanticColor.textSecondary }}>
          Manage the location information and schedules used by Beauty Burst availability services.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Store details</h2>
        <form
          onSubmit={(event) => void saveInfo(event)}
          style={{ display: 'grid', gap: space[4], marginTop: space[4] }}
        >
          <Field
            id="store-name"
            name="name"
            label="Store name"
            defaultValue={text(location.name, '')}
            required
          />
          <Field
            id="store-timezone"
            name="timezone"
            label="Timezone"
            defaultValue={text(location.timezone, '')}
            required
          />
          <Field
            id="store-address"
            name="addressLine1"
            label="Address"
            defaultValue={text(location.address_line1, '')}
          />
          <Field id="store-city" name="city" label="City" defaultValue={text(location.city, '')} />
          <Field
            id="store-region"
            name="region"
            label="State or region"
            defaultValue={text(location.region, '')}
          />
          <Field
            id="store-postal"
            name="postalCode"
            label="Postal code"
            defaultValue={text(location.postal_code, '')}
          />
          <Field
            id="store-country"
            name="countryCode"
            label="Country code"
            defaultValue={text(location.country_code, 'US')}
          />
          <Field
            id="store-phone"
            name="phone"
            label="Phone"
            defaultValue={text(location.phone, '')}
          />
          <Field
            id="store-email"
            name="email"
            type="email"
            label="Email"
            defaultValue={text(location.email, '')}
          />
          <label>
            <input
              name="pickupEnabled"
              type="checkbox"
              defaultChecked={location.pickup_enabled === true}
            />{' '}
            Pickup enabled
          </label>
          <label>
            <input
              name="deliveryEnabled"
              type="checkbox"
              defaultChecked={location.delivery_enabled === true}
            />{' '}
            Delivery enabled
          </label>
          <Button
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            type="submit"
            variant="plum"
            loading={working}
          >
            Save store details
          </Button>
        </form>
      </Card>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Weekly schedule</h2>
        <p style={{ color: semanticColor.textSecondary }}>
          Enter minutes after midnight. Leave a day blank to keep it closed.
        </p>
        <form
          onSubmit={(event) => void saveHours(event)}
          style={{ display: 'grid', gap: space[3] }}
        >
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(
            (day, index) => (
              <fieldset key={day} style={{ display: 'flex', gap: space[3], flexWrap: 'wrap' }}>
                <legend>{day}</legend>
                <Field
                  id={`open-${index}`}
                  name={`open-${index}`}
                  label="Open minute"
                  inputMode="numeric"
                />
                <Field
                  id={`close-${index}`}
                  name={`close-${index}`}
                  label="Close minute"
                  inputMode="numeric"
                />
              </fieldset>
            ),
          )}
          <Button
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            type="submit"
            variant="plum"
            loading={working}
          >
            Save weekly schedule
          </Button>
        </form>
      </Card>
      <div style={gridStyle}>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Holiday override</h2>
          <form
            onSubmit={(event) => void holiday(event)}
            style={{ display: 'grid', gap: space[3] }}
          >
            <Field id="holiday-date" name="date" type="date" label="Date" required />
            <Field id="holiday-label" name="label" label="Label" required />
            <label>
              <input name="closed" type="checkbox" defaultChecked /> Closed all day
            </label>
            <Field id="holiday-open" name="open" label="Open minute" inputMode="numeric" />
            <Field id="holiday-close" name="close" label="Close minute" inputMode="numeric" />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="secondary"
            >
              Save override
            </Button>
          </form>
        </Card>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Temporary closure</h2>
          <form
            onSubmit={(event) => void closure(event)}
            style={{ display: 'grid', gap: space[3] }}
          >
            <Field id="closure-reason" name="reason" label="Reason" required />
            <Field
              id="closure-start"
              name="startsAt"
              type="datetime-local"
              label="Starts"
              required
            />
            <Field id="closure-end" name="endsAt" type="datetime-local" label="Ends" />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="secondary"
            >
              Save closure
            </Button>
          </form>
        </Card>
        <Card>
          <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Pause store availability</h2>
          <form
            onSubmit={(event) => void pauseHours(event)}
            style={{ display: 'grid', gap: space[3] }}
          >
            <Field
              id="store-pause-until"
              name="pausedUntil"
              type="datetime-local"
              label="Pause until (blank to resume)"
            />
            <Field id="store-pause-reason" name="reason" label="Reason" />
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              type="submit"
              variant="secondary"
            >
              Save availability pause
            </Button>
          </form>
        </Card>
      </div>
      <StateMessage message={message} />
    </div>
  );
}

export function AfterHoursScreen({
  enrollment,
  client,
}: {
  enrollment?: AfterHoursEnrollment;
  client: RetailerApiClient;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  if (enrollment === undefined) return <Skeleton label="Loading Beauty After Hours" />;
  const location = enrollment.locations[0];
  if (location === undefined)
    return (
      <EmptyState
        title="No eligible locations"
        description="Add a retailer location before requesting Beauty After Hours."
      />
    );
  const request = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setWorking(true);
    try {
      await client.afterHours.request({
        locationId: location.locationId,
        requestKind: String(f.get('kind')),
        staffingPlan: String(f.get('staffingPlan')),
        expectedCapacity: Number(f.get('capacity')),
      });
      setMessage('Request submitted for founder decision. Approval is not automatic.');
    } catch {
      setMessage('The request could not be submitted.');
    } finally {
      setWorking(false);
    }
  };
  const pause = async () => {
    if (client.afterHours.pause === undefined) {
      setMessage('Pause controls are unavailable for this account.');
      return;
    }
    setWorking(true);
    try {
      await client.afterHours.pause({
        locationId: location.locationId,
        pausedUntil: location.pause.paused
          ? null
          : new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        reason: location.pause.paused ? null : 'Store-requested pause',
      });
      setMessage(
        location.pause.paused
          ? 'Beauty After Hours resume submitted.'
          : 'Beauty After Hours pause submitted.',
      );
    } catch {
      setMessage('The pause setting could not be saved.');
    } finally {
      setWorking(false);
    }
  };
  return (
    <div style={pageStyle}>
      <PageTitle title="Beauty After Hours">
        <p style={{ color: semanticColor.textSecondary }}>
          Founder approval is required. Requests never automatically make your store available
          overnight.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>{location.locationName}</h2>
        <p>
          Eligibility:{' '}
          <Badge variant={location.eligible ? 'success' : 'warning'}>
            {location.eligible ? 'Eligible' : 'Not eligible'}
          </Badge>
        </p>
        <p>
          Request status:{' '}
          <Badge variant={statusVariant(location.request?.status ?? 'NOT_REQUESTED')}>
            {location.request?.status ?? 'NOT_REQUESTED'}
          </Badge>
        </p>
        <p>
          Approved hours: late night{' '}
          {location.approvedHours.lateNight ? 'approved' : 'not approved'}; overnight{' '}
          {location.approvedHours.overnight ? 'approved' : 'not approved'}.
        </p>
        <p>
          Capacity: {location.approvedHours.capacityOrders}; staffing confirmed:{' '}
          {location.approvedHours.staffingConfirmed ? 'yes' : 'no'}.
        </p>
        <Button
          style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
          variant="secondary"
          loading={working}
          onClick={() => void pause()}
        >
          {location.pause.paused ? 'Resume approved hours' : 'Pause approved hours'}
        </Button>
      </Card>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Request review</h2>
        <form
          onSubmit={(event) => void request(event)}
          style={{ display: 'grid', gap: space[4], marginTop: space[4] }}
        >
          <label>
            Requested program
            <select name="kind" defaultValue="OVERNIGHT">
              <option value="LATE_NIGHT">Late night</option>
              <option value="OVERNIGHT">Overnight</option>
            </select>
          </label>
          <Field
            id="after-hours-staffing"
            name="staffingPlan"
            label="Staffing plan"
            defaultValue={location.request?.staffingPlan ?? ''}
            required
          />
          <Field
            id="after-hours-capacity"
            name="capacity"
            label="Expected order capacity"
            inputMode="numeric"
            defaultValue={location.request?.expectedCapacity ?? undefined}
            required
          />
          <Button
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            type="submit"
            variant="plum"
            loading={working}
          >
            Request founder review
          </Button>
        </form>
      </Card>
      <StateMessage message={message} />
    </div>
  );
}

export function PayoutsScreen({ overview }: { overview?: RetailerPayoutOverview }) {
  if (overview === undefined) return <Skeleton label="Loading payout history" />;
  const payouts = overview.payouts;
  return (
    <div style={pageStyle}>
      <PageTitle title="Payouts">
        <p style={{ color: semanticColor.textSecondary }}>
          Read-only earnings and payout history. Payout changes are not available from this portal.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Payout history</h2>
        <DataTable
          caption="Retailer payout history"
          rows={payouts}
          rowKey={(row) => text(row.id)}
          emptyMessage="No payout history is available from the server."
          columns={[
            { key: 'created', header: 'Created', render: (row) => text(row.created_at) },
            {
              key: 'status',
              header: 'Status',
              render: (row) => (
                <Badge variant={statusVariant(text(row.status))}>{text(row.status)}</Badge>
              ),
            },
            {
              key: 'commission',
              header: 'Commission',
              render: (row) => money(row.commission_minor, row.currency),
            },
            {
              key: 'net',
              header: 'Payout',
              render: (row) => money(row.payout_minor, row.currency),
            },
          ]}
        />
      </Card>
    </div>
  );
}

export function ImportsScreen({ client }: { client: RetailerApiClient }) {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [templates, setTemplates] = useState<ImportTemplate[]>([]);
  const [current, setCurrent] = useState<ImportJob | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping[]>([]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [summary, setSummary] = useState<Record<string, number> | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [working, setWorking] = useState(false);
  const refresh = async () => {
    try {
      const [nextJobs, nextTemplates] = await Promise.all([
        client.imports.list(),
        client.imports.templates(),
      ]);
      setJobs(nextJobs);
      setTemplates(nextTemplates);
      setError(false);
    } catch {
      setError(true);
    }
  };
  useEffect(() => {
    void refresh();
  }, []);
  const upload = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const selected = f.get('file');
    if (selected === null || typeof selected !== 'object' || !('name' in selected)) {
      setMessage('Choose a CSV or XLSX file.');
      return;
    }
    const file = selected as File;
    setWorking(true);
    try {
      const bytes =
        typeof file.arrayBuffer === 'function'
          ? await file.arrayBuffer()
          : await new Response(file).arrayBuffer();
      const result = await client.imports.upload({
        locationId: String(f.get('locationId')),
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileBase64: btoa(String.fromCharCode(...new Uint8Array(bytes))),
      });
      setCurrent(result.job);
      setMapping(result.mappings);
      setRows([]);
      setSummary(null);
      setMessage('File uploaded. Review the column mapping before validation.');
      await refresh();
    } catch {
      setMessage(
        'The import could not be uploaded. Confirm the file type and enabled import flag.',
      );
    } finally {
      setWorking(false);
    }
  };
  const map = async (templateId?: string) => {
    if (current === null) return;
    setWorking(true);
    try {
      await client.imports.map(current.id, templateId ? { mapping, templateId } : { mapping });
      setMessage('Column mapping saved. Validate the rows next.');
    } catch {
      setMessage('Column mapping could not be saved.');
    } finally {
      setWorking(false);
    }
  };
  const saveTemplate = async () => {
    if (templateName.trim() === '') {
      setMessage('Name the mapping template before saving it.');
      return;
    }
    try {
      await client.imports.saveTemplate({
        name: templateName.trim(),
        format: current?.file_format ?? null,
        mapping,
      });
      setMessage('Mapping template saved.');
      await refresh();
    } catch {
      setMessage('The mapping template could not be saved.');
    }
  };
  const updateTemplate = async (template: ImportTemplate) => {
    try {
      await client.imports.updateTemplate(template.id, {
        name: template.name,
        format: current?.file_format ?? template.file_format,
        mapping,
      });
      setMessage(`${template.name} updated with the current mapping.`);
      await refresh();
    } catch {
      setMessage('The mapping template could not be updated.');
    }
  };
  const validate = async () => {
    if (current === null) return;
    setWorking(true);
    try {
      const [nextSummary, nextRows] = await Promise.all([
        client.imports.validate(current.id),
        client.imports.rows(current.id),
      ]);
      setSummary(nextSummary);
      setRows(nextRows);
      setMessage('Validation complete. Errors block publishing; warnings do not.');
    } catch {
      setMessage('Validation could not be completed.');
    } finally {
      setWorking(false);
    }
  };
  const fix = async (row: ImportRow) => {
    if (current === null) return;
    try {
      await client.imports.fixRow(current.id, row.id, { normalized: row.normalized });
      setRows(await client.imports.rows(current.id));
      setMessage(`Row ${row.row_number} saved for revalidation.`);
    } catch {
      setMessage(`Row ${row.row_number} could not be updated.`);
    }
  };
  const preview = async () => {
    if (current === null) return;
    try {
      setSummary(await client.imports.preview(current.id));
      setMessage('Publish preview loaded. Review counts before publishing.');
    } catch {
      setMessage('Preview could not be loaded.');
    }
  };
  const publish = async () => {
    if (current === null) return;
    setWorking(true);
    try {
      await client.imports.publish(current.id);
      setMessage('Publish started. Refresh the import list for server progress.');
      await refresh();
    } catch {
      setMessage('Publishing could not start. Resolve blocking errors first.');
    } finally {
      setWorking(false);
    }
  };
  const report = async () => {
    if (current === null) return;
    try {
      const content = await client.imports.report(current.id);
      const href = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
      const download = document.createElement('a');
      download.href = href;
      download.download = `beauty-burst-import-${current.id}-report.csv`;
      download.click();
      URL.revokeObjectURL(href);
      setMessage('Import report downloaded.');
    } catch {
      setMessage('The import report could not be downloaded.');
    }
  };
  const cancel = async () => {
    if (current === null) return;
    try {
      await client.imports.cancel(current.id);
      setMessage('Import cancelled.');
      setCurrent(null);
      setRows([]);
      setSummary(null);
      await refresh();
    } catch {
      setMessage('Import could not be cancelled.');
    }
  };
  if (error)
    return (
      <ErrorState
        headingLevel={1}
        title="Imports unavailable"
        description="We could not load retailer imports. Verify your import permissions and try again."
      />
    );
  return (
    <div style={pageStyle}>
      <PageTitle title="Catalog and inventory imports">
        <p style={{ color: semanticColor.textSecondary }}>
          CSV and XLSX processing is available only when the retailer CSV/XLSX and bulk import flags
          are enabled.
        </p>
      </PageTitle>
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>1. Upload file</h2>
        <form
          onSubmit={(event) => void upload(event)}
          style={{ display: 'grid', gap: space[4], marginTop: space[4] }}
        >
          <Field id="import-location" name="locationId" label="Location ID" required />
          <label htmlFor="import-file">CSV or XLSX file</label>
          <input
            id="import-file"
            name="file"
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            required
          />
          <Button
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            type="submit"
            variant="plum"
            loading={working}
          >
            Upload and inspect columns
          </Button>
        </form>
      </Card>
      {current !== null && (
        <>
          <Card>
            <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>2. Map columns</h2>
            {mapping.length === 0 ? (
              <EmptyState
                title="No columns found"
                description="Upload a supported file with a header row to map its columns."
              />
            ) : (
              mapping.map((item, index) => (
                <label
                  key={`${item.sourceColumn}-${item.sourceIndex}`}
                  style={{ display: 'grid', gap: space[1] }}
                >
                  {item.sourceColumn}
                  <select
                    value={item.targetField ?? ''}
                    onChange={(event) =>
                      setMapping(
                        mapping.map((value, i) =>
                          i === index
                            ? {
                                ...value,
                                targetField: event.target.value || null,
                                isIgnored: event.target.value === '',
                              }
                            : value,
                        ),
                      )
                    }
                  >
                    <option value="">Ignore</option>
                    {[
                      'sellerSku',
                      'productName',
                      'brand',
                      'price',
                      'salePrice',
                      'onHand',
                      'lowStockThreshold',
                      'overnightEligible',
                    ].map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </label>
              ))
            )}
            <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap', marginTop: space[4] }}>
              <Button
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                variant="plum"
                loading={working}
                onClick={() => void map()}
              >
                Save mapping
              </Button>
              {templates.map((template) => (
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  key={template.id}
                  variant="secondary"
                  loading={working}
                  onClick={() => void map(template.id)}
                >
                  Apply {template.name}
                </Button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap', marginTop: space[3] }}>
              <label>
                Template name
                <input
                  aria-label="Mapping template name"
                  value={templateName}
                  onChange={(event) => setTemplateName(event.target.value)}
                />
              </label>
              <Button
                style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                variant="secondary"
                onClick={() => void saveTemplate()}
              >
                Save mapping template
              </Button>
              {templates.map((template) => (
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  key={`update-${template.id}`}
                  variant="secondary"
                  onClick={() => void updateTemplate(template)}
                >
                  Update {template.name}
                </Button>
              ))}
            </div>
          </Card>
          <Card>
            <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>
              3. Validate, fix, preview, and publish
            </h2>
            <p style={{ color: semanticColor.textSecondary }}>
              Blocking errors must be resolved before publishing. Warnings can be reviewed without
              blocking a publish.
            </p>
            <Button
              style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
              variant="plum"
              loading={working}
              onClick={() => void validate()}
            >
              Validate rows
            </Button>
            {summary !== null && (
              <p>
                <strong>Blocking errors:</strong> {summary.error ?? 0} · <strong>Warnings:</strong>{' '}
                {summary.warning ?? 0} · Create: {summary.create ?? 0} · Update:{' '}
                {summary.update ?? 0}
              </p>
            )}
            {rows.length > 0 && (
              <DataTable
                caption="Import row review"
                rows={rows}
                rowKey={(row) => row.id}
                emptyMessage="No rows need review."
                columns={[
                  { key: 'row', header: 'Row', render: (row) => String(row.row_number) },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (row) => (
                      <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                    ),
                  },
                  { key: 'action', header: 'Action', render: (row) => row.action },
                  {
                    key: 'fix',
                    header: 'Fix',
                    render: (row) => (
                      <Button
                        style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                        variant="secondary"
                        onClick={() => void fix(row)}
                      >
                        Save row
                      </Button>
                    ),
                  },
                ]}
              />
            )}
            {summary !== null && (
              <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap' }}>
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  variant="secondary"
                  onClick={() => void preview()}
                >
                  Preview publish
                </Button>
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  variant="plum"
                  loading={working}
                  onClick={() => void publish()}
                  disabled={(summary.error ?? 0) > 0}
                >
                  Publish
                </Button>
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  variant="secondary"
                  onClick={() => void report()}
                >
                  Download report
                </Button>
                <Button
                  style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
                  variant="secondary"
                  onClick={() => void cancel()}
                >
                  Cancel import
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
      <Card>
        <h2 style={{ ...typeStyle('headingSm'), margin: 0 }}>Import history</h2>
        <DataTable
          caption="Retailer import history"
          rows={jobs}
          rowKey={(job) => job.id}
          emptyMessage="No imports yet. Upload a catalog or inventory file to begin."
          columns={[
            { key: 'id', header: 'Job', render: (job) => job.id },
            { key: 'format', header: 'Format', render: (job) => job.file_format },
            { key: 'step', header: 'Step', render: (job) => job.step },
            {
              key: 'status',
              header: 'Status',
              render: (job) => <Badge variant={statusVariant(job.status)}>{job.status}</Badge>,
            },
            { key: 'errors', header: 'Errors', render: (job) => String(job.error_count) },
          ]}
        />
      </Card>
      <StateMessage message={message} />
    </div>
  );
}

export function SignInScreen({
  client,
  onSignedIn,
}: {
  client: RetailerApiClient;
  onSignedIn?: () => void;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setWorking(true);
    try {
      const result = await client.auth.signIn({
        email: String(f.get('email')),
        password: String(f.get('password')),
      });
      if (result.ok) {
        onSignedIn?.();
        setMessage('Signed in. Loading your retailer portal.');
      } else setMessage('We could not sign you in. Try again shortly.');
    } catch (error) {
      setMessage(
        error instanceof Error && 'code' in error && error.code === 'UNAUTHENTICATED'
          ? 'Email or password is incorrect.'
          : 'We could not sign you in. Check your connection and try again.',
      );
    } finally {
      setWorking(false);
    }
  };
  return (
    <section
      aria-labelledby="retailer-sign-in-title"
      style={{ display: 'grid', gap: space[6], maxWidth: '30rem', margin: `${space[12]} auto` }}
    >
      <div>
        <h1 id="retailer-sign-in-title" style={{ ...typeStyle('headingMd'), margin: 0 }}>
          Retailer sign in
        </h1>
        <p style={{ color: semanticColor.textSecondary }}>
          Use the account assigned to your Beauty Burst retailer organization.
        </p>
        <a
          href={retailerHref('/partner')}
          style={{
            display: 'inline-flex',
            minWidth: minTouchTarget,
            minHeight: minTouchTarget,
            alignItems: 'center',
          }}
        >
          Own a Beauty Supply Store? Partner With Beauty Burst.
        </a>
      </div>
      <Card>
        <form onSubmit={(event) => void submit(event)} style={{ display: 'grid', gap: space[4] }}>
          <Field
            id="retailer-email"
            name="email"
            type="email"
            label="Email"
            required
            autoComplete="email"
          />
          <Field
            id="retailer-password"
            name="password"
            type="password"
            label="Password"
            required
            autoComplete="current-password"
          />
          <Button
            style={{ minWidth: minTouchTarget, minHeight: minTouchTarget }}
            type="submit"
            variant="plum"
            loading={working}
          >
            Sign in
          </Button>
        </form>
      </Card>
      {message === null ? null : message.startsWith('Signed in') ? (
        <StateMessage message={message} />
      ) : (
        <div role="alert">
          <strong>Unable to sign in</strong>
          <br />
          {message}
        </div>
      )}
    </section>
  );
}
export function NotFoundScreen() {
  return (
    <EmptyState
      headingLevel={1}
      title="Page not found"
      description="Choose a retailer portal destination from the navigation."
    />
  );
}
